import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Paper, Typography, Box, alpha, Chip } from '@mui/material';
import { CallSplit } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const DecisionNode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Get theme-aware colors
  const nodeColor = data.color || theme.palette.warning.main;
  const textColor = theme.palette.getContrastText(nodeColor);
  const borderColor = selected ? theme.palette.secondary.main : 'transparent';
  const hoverColor = alpha(nodeColor, 0.9);

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper
        elevation={selected ? 8 : 4}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        sx={{
          padding: 2,
          minWidth: 100,
          minHeight: 100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: nodeColor,
          color: textColor,
          border: `3px solid ${borderColor}`,
          cursor: 'move',
          position: 'relative',
          // Diamond shape using clip-path
          clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
          transition: theme.transitions.create([
            'transform', 'box-shadow', 'background-color'
          ], {
            duration: theme.transitions.duration.shorter,
          }),
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: theme.shadows[12],
            backgroundColor: hoverColor,
          },
          '&:focus-within': {
            outline: `2px solid ${theme.palette.secondary.main}`,
            outlineOffset: 4,
          },
          '&[aria-selected="true"]': {
            boxShadow: `0 0 0 4px ${alpha(theme.palette.secondary.main, 0.3)}`,
          },
        }}
        role="button"
        tabIndex={0}
        aria-label={`Decision node: ${data.label || 'Decision'}`}
        aria-selected={selected}
      >
        <Handle
          type="target"
          position={Position.Top}
          style={{
            background: theme.palette.text.primary,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 12,
            height: 12,
            top: -6,
          }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <CallSplit
            sx={{
              fontSize: 28,
              mb: 0.5,
              color: textColor,
              filter: isDark ? 'none' : 'brightness(1.1)',
            }}
          />
          <Typography
            variant="caption"
            fontWeight="600"
            sx={{
              color: textColor,
              fontSize: '0.8rem',
              lineHeight: 1.1,
              maxWidth: '70px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {data.label || 'Decision'}
          </Typography>
        </Box>

        {/* YES Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="yes"
          style={{
            background: theme.palette.success.main,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 12,
            height: 12,
            right: -6,
          }}
        />

        {/* NO Handle */}
        <Handle
          type="source"
          position={Position.Bottom}
          id="no"
          style={{
            background: theme.palette.error.main,
            border: `2px solid ${theme.palette.background.paper}`,
            width: 12,
            height: 12,
            bottom: -6,
          }}
        />
      </Paper>

      {/* YES/NO Labels */}
      <Chip
        label="YES"
        size="small"
        sx={{
          position: 'absolute',
          top: '50%',
          right: -25,
          transform: 'translateY(-50%)',
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
          fontSize: '0.6rem',
          height: 18,
          minWidth: 35,
          opacity: selected ? 1 : 0.8,
          transition: theme.transitions.create('opacity'),
        }}
      />

      <Chip
        label="NO"
        size="small"
        sx={{
          position: 'absolute',
          bottom: -25,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          fontSize: '0.6rem',
          height: 18,
          minWidth: 30,
          opacity: selected ? 1 : 0.8,
          transition: theme.transitions.create('opacity'),
        }}
      />
    </Box>
  );
});

DecisionNode.displayName = 'DecisionNode';

export default DecisionNode;